import React, { useId, useMemo, useState } from 'react';

/** Purpose: search and select a localization key for a node field. Why: a large translation catalog needs discoverable key selection instead of manual typing. */
export default function LocalizationKeyPicker({
  localizationKey = '',
  localizationKeys = [],
  onKeyChange,
  selectClass,
  label = 'Localization key',
  normalTextLabel = 'Normal text (no key)',
}) {
  const listboxId = `localization-key-suggestions-${useId()}`;
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  /** Purpose: Deduplicate and search keys while retaining the current selection. Why: A saved key must remain inspectable even if absent from the loaded catalog. */
  const visibleKeys = useMemo(() => {
    const normalizedSearch = search.trim().toLocaleLowerCase();
    const keys = [...new Set(localizationKeys)];
    if (localizationKey && !keys.includes(localizationKey)) keys.unshift(localizationKey);
    if (!normalizedSearch) return keys;

    return keys
      .filter((key) => String(key).toLocaleLowerCase().includes(normalizedSearch))
      .sort((left, right) => {
        const leftStartsWith = String(left).toLocaleLowerCase().startsWith(normalizedSearch);
        const rightStartsWith = String(right).toLocaleLowerCase().startsWith(normalizedSearch);
        return Number(rightStartsWith) - Number(leftStartsWith);
      });
  }, [localizationKey, localizationKeys, search]);
  const normalizedSearch = search.trim().toLocaleLowerCase();
  const suggestions = normalizedSearch ? visibleKeys.slice(0, 8) : [];

  /** Purpose: apply a selected key and reset the autocomplete. Why: choosing a result should keep the existing key-change flow predictable. */
  const chooseKey = (key) => {
    onKeyChange(key);
    setSearch('');
    setActiveIndex(0);
  };

  /** Purpose: highlight the typed portion of a suggested key. Why: authors can scan matching results quickly in long catalogs. */
  const renderKeyMatch = (key) => {
    const matchIndex = String(key).toLocaleLowerCase().indexOf(normalizedSearch);
    if (matchIndex < 0 || !normalizedSearch) return key;

    return <>
      {key.slice(0, matchIndex)}
      <mark className="rounded bg-cyan-100 px-0.5 text-cyan-950 dark:bg-cyan-400/25 dark:text-cyan-100">
        {key.slice(matchIndex, matchIndex + search.trim().length)}
      </mark>
      {key.slice(matchIndex + search.trim().length)}
    </>;
  };

  return <div className="space-y-2">
    <div className="relative">
      <input
        type="search"
        role="combobox"
        aria-label={`Search ${label.toLocaleLowerCase()}s`}
        aria-autocomplete="list"
        aria-expanded={Boolean(normalizedSearch)}
        aria-controls={normalizedSearch ? listboxId : undefined}
        aria-activedescendant={suggestions[activeIndex] ? `${listboxId}-option-${activeIndex}` : undefined}
        placeholder="Search localization keys…"
        value={search}
        onChange={(event) => {
          setSearch(event.target.value);
          setActiveIndex(0);
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' && suggestions.length) {
            event.preventDefault();
            setActiveIndex((index) => Math.min(index + 1, suggestions.length - 1));
          } else if (event.key === 'ArrowUp' && suggestions.length) {
            event.preventDefault();
            setActiveIndex((index) => Math.max(index - 1, 0));
          } else if (event.key === 'Enter' && suggestions[activeIndex]) {
            event.preventDefault();
            chooseKey(suggestions[activeIndex]);
          } else if (event.key === 'Escape' && normalizedSearch) {
            event.preventDefault();
            setSearch('');
            setActiveIndex(0);
          }
        }}
        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/10 dark:border-white/10 dark:bg-slate-900 dark:text-slate-100"
      />
      {normalizedSearch ? (
        <div
          id={listboxId}
          className="absolute inset-x-0 top-full z-50 mt-1 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl dark:border-white/10 dark:bg-slate-900"
        >
          {suggestions.length ? (
            <>
              <div className="border-b border-slate-100 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:border-white/10 dark:text-slate-400">
                Matching keys
              </div>
              <div role="listbox" aria-label="Matching localization keys" className="max-h-52 overflow-y-auto p-1">
                {suggestions.map((key, index) => (
                  <button
                    id={`${listboxId}-option-${index}`}
                    key={key}
                    type="button"
                    role="option"
                    aria-selected={index === activeIndex}
                    onMouseEnter={() => setActiveIndex(index)}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => chooseKey(key)}
                    className={`block w-full rounded-lg px-2.5 py-2 text-left font-mono text-xs transition ${
                      index === activeIndex
                        ? 'bg-cyan-50 text-cyan-900 dark:bg-cyan-500/15 dark:text-cyan-100'
                        : 'text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-white/5'
                    }`}
                  >
                    {renderKeyMatch(key)}
                    {!localizationKeys.includes(key) ? <span className="ml-2 font-sans text-[10px] text-amber-600 dark:text-amber-300">Missing</span> : null}
                  </button>
                ))}
              </div>
              {visibleKeys.length > suggestions.length ? (
                <div className="border-t border-slate-100 px-3 py-1.5 text-[10px] text-slate-500 dark:border-white/10 dark:text-slate-400">
                  Showing {suggestions.length} of {visibleKeys.length}; type more to narrow results.
                </div>
              ) : null}
            </>
          ) : (
            <div className="px-3 py-2.5 text-xs text-slate-500 dark:text-slate-400">No matching localization keys.</div>
          )}
        </div>
      ) : null}
    </div>
    <select
      aria-label={label}
      className={selectClass}
      value={localizationKey}
      onChange={(event) => {
        chooseKey(event.target.value);
      }}
    >
      <option value="">{normalTextLabel}</option>
      {visibleKeys.length
        ? visibleKeys.map((key) => <option key={key} value={key}>{key}{!localizationKeys.includes(key) ? ' (missing key)' : ''}</option>)
        : <option value="" disabled>No matching localization keys</option>}
    </select>
  </div>;
}
